import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, User, Share2, Twitter, Linkedin, Facebook } from 'lucide-react';
import { getPostBySlug, getRelatedPosts } from '../data/blogData';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const relatedPosts = getRelatedPosts(slug, 3);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F4F4F3] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#1C1917] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Article Not Found
          </h1>
          <p className="text-[#78716C] mb-6">The article you're looking for doesn't exist.</p>
          <Link 
            to="/#blog"
            className="bg-[#AA94FF] text-[#F4F4F3] px-6 py-3 rounded-full font-semibold hover:bg-[#CC5003] transition-colors"
          >
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const shareUrl = window.location.href;
  const shareText = `Check out this article: ${post.title}`;

  return (
    <div className="min-h-screen bg-[#F4F4F3]">
      <Header />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/80 via-[#1C1917]/40 to-transparent" />
          
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-12">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Link 
                  to="/#blog"
                  className="inline-flex items-center gap-2 text-[#F4F4F3]/80 hover:text-[#F4F4F3] transition-colors mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Blog
                </Link>
                
                <span className="block text-[#AA94FF] text-sm font-bold uppercase tracking-wider mb-3">
                  {post.category}
                </span>
                
                <h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F4F4F3] leading-tight"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {post.title}
                </h1>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-12 px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            {/* Meta Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-[#1C1917]/10"
            >
              <div className="flex items-center gap-3">
                <img
                  src={post.authorImage}
                  alt={post.author}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-[#1C1917]">{post.author}</p>
                  <p className="text-sm text-[#78716C]">Founder, HypeLab Marketing</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-[#78716C]">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.date)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {post.readTime}
                </span>
              </div>

              {/* Share Buttons */}
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-[#78716C] mr-2">Share:</span>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-[#1C1917]/10 flex items-center justify-center hover:bg-[#AA94FF] hover:text-[#F4F4F3] transition-colors"
                >
                  <Twitter className="w-4 h-4" />
                </a>
                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-[#1C1917]/10 flex items-center justify-center hover:bg-[#AA94FF] hover:text-[#F4F4F3] transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-[#1C1917]/10 flex items-center justify-center hover:bg-[#AA94FF] hover:text-[#F4F4F3] transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </motion.div>

            {/* Article Body */}
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="prose prose-lg max-w-none"
            >
              <div 
                className="text-[#1C1917] leading-relaxed space-y-6"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {post.content.split('\n').map((paragraph, index) => {
                  if (paragraph.startsWith('## ')) {
                    return (
                      <h2 
                        key={index}
                        className="text-2xl sm:text-3xl font-bold text-[#1C1917] mt-10 mb-4"
                        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                      >
                        {paragraph.replace('## ', '')}
                      </h2>
                    );
                  }
                  if (paragraph.startsWith('### ')) {
                    return (
                      <h3 
                        key={index}
                        className="text-xl sm:text-2xl font-bold text-[#1C1917] mt-8 mb-3"
                        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                      >
                        {paragraph.replace('### ', '')}
                      </h3>
                    );
                  }
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return (
                      <p key={index} className="font-bold text-[#1C1917] mt-6 mb-2">
                        {paragraph.replace(/\*\*/g, '')}
                      </p>
                    );
                  }
                  if (paragraph.startsWith('- ')) {
                    return (
                      <li key={index} className="ml-6 text-[#78716C]">
                        {paragraph.replace('- ', '')}
                      </li>
                    );
                  }
                  if (paragraph.match(/^\d+\./)) {
                    return (
                      <li key={index} className="ml-6 text-[#78716C] list-decimal">
                        {paragraph.replace(/^\d+\.\s*/, '')}
                      </li>
                    );
                  }
                  if (paragraph.trim() === '') {
                    return null;
                  }
                  // Handle links
                  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
                  if (paragraph.match(linkRegex)) {
                    const parts = paragraph.split(linkRegex);
                    return (
                      <p key={index} className="text-[#78716C] leading-relaxed">
                        {paragraph.replace(linkRegex, (match, text, url) => {
                          return `<a href="${url}" class="text-[#AA94FF] hover:underline">${text}</a>`;
                        }).split(/<a |<\/a>/).map((part, i) => {
                          if (part.startsWith('href=')) {
                            const hrefMatch = part.match(/href="([^"]+)"/);
                            const textMatch = part.match(/>([^<]+)/);
                            if (hrefMatch && textMatch) {
                              return (
                                <Link key={i} to={hrefMatch[1]} className="text-[#AA94FF] hover:underline">
                                  {textMatch[1]}
                                </Link>
                              );
                            }
                          }
                          return part;
                        })}
                      </p>
                    );
                  }
                  return (
                    <p key={index} className="text-[#78716C] leading-relaxed">
                      {paragraph}
                    </p>
                  );
                })}
              </div>
            </motion.article>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-12 p-8 bg-[#1C1917] rounded-3xl text-center"
            >
              <h3 
                className="text-2xl font-bold text-[#F4F4F3] mb-3"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                Ready to Transform Your Digital Presence?
              </h3>
              <p className="text-[#F4F4F3]/70 mb-6">
                Let's work together to achieve your marketing goals.
              </p>
              <Link
                to="/#contact"
                className="inline-block bg-[#AA94FF] text-[#F4F4F3] px-8 py-4 rounded-full font-bold uppercase tracking-wider hover:bg-[#CC5003] transition-colors"
              >
                Get in Touch
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="py-16 px-6 sm:px-8 lg:px-12 bg-[#EDE5D4]">
            <div className="max-w-7xl mx-auto">
              <h2 
                className="text-3xl font-bold text-[#1C1917] mb-8"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                Related Articles
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden border border-[#1C1917]/5 hover:shadow-lg transition-shadow"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#AA94FF]">
                        {relatedPost.category}
                      </span>
                      <h3 
                        className="text-lg font-bold text-[#1C1917] mt-2 group-hover:text-[#AA94FF] transition-colors line-clamp-2"
                        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                      >
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-[#78716C] mt-2">{relatedPost.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;
